/*
 * Copyright (c) 2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, { useMemo, useState } from 'react'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import { useTypedSelector } from 'helpers/reducers'
import { useAppDispatch } from 'helpers/store'
import UiFormModalShell from 'components/Forms/UiFormModalShell'
import UiFormInner from 'components/Forms/UIFormInner'
import UiButton, { UiButtonSize, UiButtonStyle } from 'components/Ui/UiButton'
import UiInput from 'components/Ui/UiInput'
import UiCheckbox from 'components/Ui/UiCheckbox'
import {
    getDefaultRejectionReasons,
    resolveRejectionReasons,
    resetRejectionReasonsCatalogToDefaults,
    saveRejectionReasonsCatalog,
    selectRejectionReasonsCatalog,
    StoredRejectionReason,
} from 'utils/rejectionReasonsCatalog'

type RejectionReasonsCatalogModalProps = {
    onClose: () => void
}

const RejectionReasonsCatalogModal: React.FC<RejectionReasonsCatalogModalProps> = ({
    onClose,
}) => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const storedCatalog = useTypedSelector(selectRejectionReasonsCatalog)
    const initialReasons = useMemo(
        () => resolveRejectionReasons(t, storedCatalog),
        [storedCatalog, t]
    )
    const [reasons, setReasons] = useState<StoredRejectionReason[]>(initialReasons)
    const normalizedIds = reasons.map((reason) => reason.id.trim()).filter(Boolean)
    const hasInvalidReasons =
        reasons.some((reason) => !reason.id.trim() || !reason.label.trim()) ||
        new Set(normalizedIds).size !== normalizedIds.length

    const updateReason = (
        index: number,
        patch: Partial<StoredRejectionReason>
    ) => {
        setReasons((prev) =>
            prev.map((reason, reasonIndex) =>
                reasonIndex === index ? { ...reason, ...patch } : reason
            )
        )
    }

    const addReason = () => {
        setReasons((prev) => [
            ...prev,
            {
                id: `custom_${Date.now()}`,
                label: '',
            },
        ])
    }

    const removeReason = (index: number) => {
        setReasons((prev) => prev.filter((_, reasonIndex) => reasonIndex !== index))
    }

    const handleSave = () => {
        const cleaned = reasons
            .map((reason) => ({
                ...reason,
                id: reason.id.trim(),
                label: reason.label.trim(),
            }))
            .filter((reason) => reason.id && reason.label)

        saveRejectionReasonsCatalog(dispatch, cleaned)
        onClose()
    }

    const handleReset = () => {
        resetRejectionReasonsCatalogToDefaults(dispatch)
        setReasons(getDefaultRejectionReasons(t))
    }

    return (
        <UiFormModalShell
            className={clsx('RejectionReasonsCatalogModal')}
            title={t('Rejection reasons catalog')}
            subtitle={t('Customize rejection reasons used in lead and offer flows')}
            showHeader
            footer={
                <div className={clsx('flex items-center justify-between w-full')}>
                    <UiButton
                        size={UiButtonSize.Normal}
                        style={UiButtonStyle.Secondary}
                        title={t('Reset to defaults') as string}
                        onClick={handleReset}
                    />
                    <div className={clsx('flex items-center gap-2')}>
                        <UiButton
                            size={UiButtonSize.Normal}
                            style={UiButtonStyle.Secondary}
                            title={t('Cancel') as string}
                            onClick={onClose}
                        />
                        <UiButton
                            size={UiButtonSize.Normal}
                            style={UiButtonStyle.Primary}
                            title={t('Save') as string}
                            onClick={handleSave}
                            disabled={hasInvalidReasons}
                        />
                    </div>
                </div>
            }
        >
            <UiFormInner>
                <div className={clsx('space-y-3')}>
                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200">
                        {t('This catalog is stored only in this browser and is not shared with other users.')}
                    </div>
                    {hasInvalidReasons && (
                        <div className="text-sm text-red-600 dark:text-red-400">
                            {t('Every rejection reason needs a unique ID and a label.')}
                        </div>
                    )}
                    {reasons.map((reason, index) => (
                        <div
                            key={`${reason.id}-${index}`}
                            className={clsx(
                                'grid grid-cols-[1fr_2fr_auto_auto] gap-2 items-center'
                            )}
                        >
                            <UiInput
                                value={reason.id}
                                onChange={(event) =>
                                    updateReason(index, { id: event.target.value })
                                }
                                placeholder={t('Reason id')}
                            />
                            <UiInput
                                value={reason.label}
                                onChange={(event) =>
                                    updateReason(index, { label: event.target.value })
                                }
                                placeholder={t('Reason label')}
                            />
                            <UiCheckbox
                                name={`require_comment_${reason.id}`}
                                checked={Boolean(reason.requireComment)}
                                onChange={(event) =>
                                    updateReason(index, {
                                        requireComment: event.target.checked,
                                    })
                                }
                                label={t('Comment required')}
                            />
                            <UiButton
                                size={UiButtonSize.Small}
                                style={UiButtonStyle.Secondary}
                                title={t('Remove') as string}
                                onClick={() => removeReason(index)}
                            />
                        </div>
                    ))}
                    <UiButton
                        size={UiButtonSize.Normal}
                        style={UiButtonStyle.Secondary}
                        title={t('Add reason') as string}
                        onClick={addReason}
                    />
                </div>
            </UiFormInner>
        </UiFormModalShell>
    )
}

export default RejectionReasonsCatalogModal
